// Simple Alert System for User-Friendly Messages

class AlertSystem {
  constructor() {
    this.alerts = [];
    this.listeners = [];
  }

  // Show different types of alerts
  showSuccess(message, duration = 5000) {
    this.showAlert('success', message, duration);
  }

  showError(message, duration = 8000) {
    this.showAlert('error', message, duration);
  }

  showWarning(message, duration = 6000) {
    this.showAlert('warning', message, duration);
  }

  showInfo(message, duration = 5000) {
    this.showAlert('info', message, duration);
  }

  showAlert(type, message, duration = 5000) {
    const alert = {
      id: Date.now() + Math.random(),
      type,
      message,
      timestamp: new Date(),
    };

    this.alerts.push(alert);
    this.notifyListeners();

    // Auto-remove alert after duration
    if (duration > 0) {
      setTimeout(() => {
        this.removeAlert(alert.id);
      }, duration);
    }
  }

  removeAlert(id) {
    this.alerts = this.alerts.filter(alert => alert.id !== id);
    this.notifyListeners();
  }

  clearAllAlerts() {
    this.alerts = [];
    this.notifyListeners();
  }

  // For React components to listen to alert changes
  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  notifyListeners() {
    this.listeners.forEach(listener => listener(this.alerts));
  }

  getAlerts() {
    return this.alerts;
  }
}

// Create global alert instance
const alertSystem = new AlertSystem();

// Helper functions for common API error scenarios
export const showApiError = (error, operation = 'operation') => {
  let message = `Failed to ${operation}. Please try again.`;
  
  if (error.message) {
    if (error.message.includes('400')) {
      message = `Invalid information provided for ${operation}. Please check your input and try again.`;
    } else if (error.message.includes('401')) {
      message = `You are not authorized to perform this ${operation}. Please log in again.`;
    } else if (error.message.includes('403')) {
      message = `You don't have permission to ${operation}. Please contact your administrator.`;
    } else if (error.message.includes('404')) {
      message = `The requested information was not found. Please refresh the page and try again.`;
    } else if (error.message.includes('500')) {
      message = `Server error occurred during ${operation}. Please try again in a few minutes.`;
    } else if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
      message = `Network connection problem. Please check your internet connection and try again.`;
    } else if (error.message.includes('timeout')) {
      message = `${operation} took too long to complete. Please try again.`;
    }
  }

  alertSystem.showError(message);
};

export const showLoginError = (error) => {
  if (error.message && error.message.includes('400')) {
    alertSystem.showError('Invalid email or password. Please check your credentials and try again.');
  } else {
    showApiError(error, 'log in');
  }
};

export const showSuccess = (message) => {
  alertSystem.showSuccess(message);
};

export const showWarning = (message) => {
  alertSystem.showWarning(message);
};

export const showInfo = (message) => {
  alertSystem.showInfo(message);
};

// Export the alert system instance
export default alertSystem; 